import AuditInfo from 'components/audit/AuditInfo';
import { OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import PinButton from 'components/buttons/PinButton';
import GridItemList, { FIELD_GRID_CLASS, GridCells, infoLabel, Row, SectionCard } from 'components/GridItemList';
import Loader from 'components/Loader';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import PageHeader from 'components/PageHeader';
import { RecordChip } from 'components/record-chip/RecordChip';
import {
    META_ROW_CLASS,
    MetaItem,
    OUTLINED_ACTION_CLASS,
    PRIMARY_ACTION_CLASS,
    RECORD_GRID_CLASS,
} from 'components/record-header/RecordHeader';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { Tooltip } from 'components/ui/tooltip';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import { formatHoursToHm } from 'components/util/HoursDisplay';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useGetTimecardByIdQuery, useSaveFinishedTimecardAsDraftMutation } from 'store/api/timecards';
import { ClassifierData } from 'types/classifiers';
import { TimecardDetails, TimecardStatusType } from 'types/timecards';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { useBreadcrumbLeaf } from 'utils/breadcrumbLeaf';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { useCanReadMatterAssignments } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { FeatureFlag } from 'types/features';
import TimeCardExpenseList from './expenses/TimeCardExpenseList';
import { useDescriptionSideClass } from './form-sections/DescriptionFields';
import { TimecardGroupTitle, TimecardSection } from './form-sections/TimecardSection';
import { matterAssignmentsArgs } from './form-sections/useTimecardForm';
import TimeCardInvoiceList from './invoices/TimeCardInvoiceList';
import { TimecardStatusLabel } from './labels/TimecardStatusLabel';
import { timecardDtoToForm, timecardUpdateRequest } from './timecardFormMapping';
import { useTimecardReadRows } from './TimecardEntityView';
import { TimerLogsList } from './TimerLogsList';
import { useOpenFinishTimecard } from './useOpenFinishTimecard';
import { useTimecardFormContext } from './useTimecardFormContext';
import { FINISH_BLOCKING_MATTER_STATES, finishBlockerLines } from './utils/timecardUtils';

const HEADER_CLASSIFIERS = ['MATTER_STATE_TYPE', 'TIMECARD_VALUE_FACTORS'];

/**
 * The timecard's status actions: a draft is finished in place, off while its case's state or the backend's finish
 * blockers refuse it, with one tooltip and description listing every reason (off, it stays a Tab stop, so the list
 * opens on focus as on hover); a finished timecard goes back to draft for its creator. Saving the Timecard card keeps
 * the status.
 */
function TimecardStatusActions({
    timecard,
    classifiers,
}: {
    timecard: TimecardDetails;
    classifiers: Record<string, ClassifierData>;
}) {
    const { user } = useAuth();
    const { openFinish, isFinishing } = useOpenFinishTimecard();
    const { context, isLoading: isLoadingContext } = useTimecardFormContext();
    const { refetch } = useGetTimecardByIdQuery(timecard.id);
    const [saveFinishedTimecardAsDraft] = useSaveFinishedTimecardAsDraftMutation();
    const [isReverting, setIsReverting] = useState(false);
    const reasonId = useId();

    const status = timecard.timecardStatusTypeCode;
    const isFinishBlockedByCaseState = FINISH_BLOCKING_MATTER_STATES.includes(timecard.matter.matterStateTypeCode);

    const revertToDraft = async () => {
        setIsReverting(true);
        try {
            const stored = await refetch().unwrap();
            await saveFinishedTimecardAsDraft(
                timecardUpdateRequest(timecardDtoToForm(stored, context), stored)
            ).unwrap();
            successAlert(t('timecards.timecard-updated'));
        } catch {
            // The API layer toasts the failure.
        } finally {
            setIsReverting(false);
        }
    };

    if (status === TimecardStatusType.DRAFT) {
        const reason = [
            ...(isFinishBlockedByCaseState
                ? [
                      t('timecards.form.finish-blocked-by-case-state', {
                          status: getClassifierText(
                              classifiers['MATTER_STATE_TYPE'],
                              timecard.matter.matterStateTypeCode
                          ),
                      }),
                  ]
                : []),
            ...finishBlockerLines(timecard.finishBlockers),
        ].join('\n');
        const finish = (
            <PermissionButton
                hasAuthority={timecard.isEditable}
                variant="contained"
                color="secondary"
                size="small"
                className={cn(PRIMARY_ACTION_CLASS, OFF_SECONDARY_CLASS)}
                onClick={() => void openFinish(timecard.id)}
                disabled={!!reason || isFinishing}
                focusableWhenDisabled
                aria-describedby={reason ? reasonId : undefined}
            >
                <FormattedMessage id="timecards.finish" />
            </PermissionButton>
        );
        return reason ? (
            <>
                <Tooltip content={reason}>{finish}</Tooltip>
                <span id={reasonId} hidden>
                    {reason}
                </span>
            </>
        ) : (
            finish
        );
    }
    if (status === TimecardStatusType.FINISHED && timecard.createdByEmployee?.id === user?.id) {
        return (
            <PermissionButton
                hasAuthority={timecard.isEditable}
                variant="outlined"
                size="small"
                className={`${OUTLINED_ACTION_CLASS} px-3.5 font-medium`}
                onClick={() => void revertToDraft()}
                disabled={isLoadingContext || isReverting}
            >
                <FormattedMessage id="timecards.revert-to-draft" />
            </PermissionButton>
        );
    }
    return null;
}

function TimecardRecordHeader({
    timecard,
    classifiers,
}: {
    timecard: TimecardDetails;
    classifiers: Record<string, ClassifierData>;
}) {
    const dateFormat = useEffectiveDateFormat();
    // Every timecard's title is "Timecard", so its pin reads by its date and case.
    const date = format(new Date(timecard.timecardDate), dateFormat);
    const pinLabel = `${date} · ${timecard.matter.fullName}`;
    // The leaf crumb turns the Timecards crumb before it into the way back to the list. It names the case by its
    // number; the record's name, which the AI panel reads, keeps the full name.
    useBreadcrumbLeaf(pinLabel, timecard.matter.caseNumber ? `${date} · ${timecard.matter.caseNumber}` : undefined);
    return (
        <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-auto flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2.5">
                    <TimecardStatusLabel
                        statusCode={timecard.timecardStatusTypeCode}
                        classifier={classifiers['TIMECARD_STATUS_TYPE']}
                    />
                    <PinButton pin={recordPin('timecards', timecard.id, pinLabel)} />
                </div>
                <PageHeader titleId="timecards.timecard">
                    <div className={META_ROW_CLASS}>
                        <MetaItem label={<FormattedMessage id="timecards.form.case" />}>
                            <RecordChip entityType="MATTER" id={timecard.matter.id} label={timecard.matter.fullName} />
                        </MetaItem>
                        <MetaItem label={<FormattedMessage id="timecards.form.employee" />}>
                            {timecard.employee.name}
                        </MetaItem>
                        <MetaItem label={<FormattedMessage id="timecards.form.date" />}>
                            <FormattedDate value={timecard.timecardDate} />
                        </MetaItem>
                    </div>
                </PageHeader>
            </div>
            <div className="flex flex-none flex-row items-center justify-end gap-2">
                <TimecardStatusActions timecard={timecard} classifiers={classifiers} />
            </div>
        </div>
    );
}

/**
 * The new design's timecard: a header with the status actions, the one Timecard card, which holds every field
 * and edits in place, and the read-only cards beside it.
 */
export default function TimecardRecord({ timecard }: { timecard: TimecardDetails }) {
    const { rows, classifiers, isLoading } = useTimecardReadRows(timecard);
    const { classifiers: headerClassifiers, isLoading: isLoadingHeaderClassifiers } = useClassifiers({
        codes: HEADER_CLASSIFIERS,
    });
    const isTimecardNotesEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_NOTES);
    const descriptionSideClass = useDescriptionSideClass();
    // What the card's form asks before it draws a field, so the card lists the fields its form will show.
    const isAssignmentEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const canReadMatterAssignments = useCanReadMatterAssignments();
    const showEmptySelection = useIsFeatureEnabled(FeatureFlag.SHOW_EMPTY_SELECTION);
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    const isBilledTimeControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_BILLED_TIME_CONTROL);
    // The form's own query: a timecard without an assignment has the field when its case has assignments, and the
    // form then finds the list loaded. A timecard that cannot be edited has no form to follow.
    const { data: caseAssignments } = useGetMinAssignmentsListWithFiltersQuery(
        matterAssignmentsArgs(timecard.matter.id),
        {
            skip:
                !timecard.isEditable ||
                !isAssignmentEnabled ||
                !canReadMatterAssignments ||
                !!timecard.matterAssignment,
        }
    );

    if (isLoading || isLoadingHeaderClassifiers) {
        return <Loader />;
    }
    const allClassifiers = { ...classifiers, ...headerClassifiers };

    // The card's rows are its form's fields, group by group, in the form's order and under the form's labels.
    const hasActivityTypeField =
        showEmptySelection || !!getValidOptionsFromClassifier(allClassifiers['TIMECARD_ACTIVITY_TYPE'])?.length;
    const timecardRows: Row[] = [
        {
            label: t('timecards.form.case-client'),
            wide: true,
            // The case over its client, as the form's field has the client on a line under it.
            content: (
                <>
                    <EllipsisTooltip>{rows.case.content}</EllipsisTooltip>
                    {rows.client.content ? (
                        // The name and its link flow as text, so a long one wraps where the field's line does.
                        <div className="pt-1 font-normal [&_a]:inline [&>span]:inline">
                            {t('timecards.form.client')}: {rows.client.content}
                        </div>
                    ) : null}
                </>
            ),
        },
        {
            ...rows.assignment,
            content: rows.assignment.content ?? '-',
            hidden: !timecard.matterAssignment && !caseAssignments?.content.length,
            readOnly: !isAssignmentEnabled,
        },
        {
            ...rows.activityType,
            content: rows.activityType.content || '-',
            hidden: !rows.activityType.content && !hasActivityTypeField,
            readOnly: !hasActivityTypeField,
        },
        rows.employee,
        { ...rows.date, content: <FormattedDate value={timecard.timecardDate} /> },
        {
            ...rows.practiceAreas,
            label: infoLabel('practice-areas.practice-areas', 'timecards.tooltips.practice-areas', 'justify-between'),
            content: '-',
            chips: timecard.practiceAreas.map((area) => ({ key: area.id, label: formatPracticeAreaLabel(area) })),
        },
    ];
    const usedHours = timecard.timecardStats?.totalTimeInHours || 0;
    const usedMinutesHint = formatHoursToHm(usedHours);
    // The form has the uplift fields while the billed time is above the used time.
    const hasUpliftFields = isBilledTimeControlEnabled && (timecard.timecardStats?.billedTimeInHours || 0) > usedHours;
    const valueFactors = timecard.billingUpliftValueFactors ?? [];
    const billingRows: Row[] = [
        {
            ...rows.nonBillable,
            label: infoLabel('timecards.form.is-non-billable', 'timecards.tooltips.is-non-billable', 'justify-between'),
        },
        {
            ...rows.preserveNonBillableHours,
            label: infoLabel(
                'timecards.preserve-non-billable-hours',
                'timecards.tooltips.preserve-non-billable-hours',
                'justify-between'
            ),
            readOnly: hideDontNullifyOption,
        },
        {
            label: t('timecards.form.total-time'),
            // With the minutes under the hours, as the field has them.
            content: (
                <>
                    {rows.totalTime.content}
                    {usedMinutesHint ? (
                        <span className="text-fg-secondary mt-0.5 block text-xs font-normal whitespace-nowrap tabular-nums">
                            {usedMinutesHint}
                        </span>
                    ) : null}
                </>
            ),
        },
        { ...rows.billedTime, label: t('timecards.form.billed-time') },
        {
            label: infoLabel(
                'timecards.form.billing-uplift-value-factors',
                'timecards.tooltips.billing-uplift-value-factors',
                'justify-between'
            ),
            content: '-',
            chips: valueFactors.map((code) => ({
                key: code,
                label: getClassifierText(allClassifiers['TIMECARD_VALUE_FACTORS'], code),
            })),
            hidden: !valueFactors.length && !hasUpliftFields,
        },
        {
            label: t('timecards.form.value-factors-notes'),
            content: timecard.valueFactorsNotes || '-',
            multiline: true,
            hidden: !timecard.valueFactorsNotes && !hasUpliftFields,
        },
        // Each on a row of its own, as the form has them.
        { ...rows.remunerationTime, wide: true },
        { ...rows.multiplier, wide: true },
    ];
    const descriptionRows: Row[] = [
        {
            label: t('timecards.form.description'),
            content: timecard.description || '-',
            multiline: true,
            // In the box the form's buttons take beside its text area, so the text wraps where it does there.
            button: (
                <div className={descriptionSideClass}>
                    {timecard.description ? (
                        <CopyToClipboardIcon text={timecard.description} className="-my-0.5" />
                    ) : null}
                </div>
            ),
        },
        {
            label: t('timecards.form.notes'),
            content: timecard.notes || '-',
            multiline: true,
            hidden: !isTimecardNotesEnabled,
        },
    ];
    const expensesRow: Row = {
        label: '',
        wide: true,
        content: timecard.expenses?.length ? (
            <TimeCardExpenseList
                timecardData={{ id: timecard.id, matterId: timecard.matter.id }}
                classifiers={classifiers}
                expenses={timecard.expenses}
                disableAdd
            />
        ) : (
            '-'
        ),
    };
    // The rows no field edits come last, after every group, where the card's form has them under its fields.
    const editable = (group: Row[]) => group.filter((row) => !row.readOnly);
    const readOnlyRows = [...timecardRows, ...billingRows].filter((row) => row.readOnly);

    return (
        <PaddedLayout className="new:p-0">
            <div className="bg-card border-border border-b px-(--page-gutter-x) pt-(--page-gutter-y) pb-4">
                <TimecardRecordHeader timecard={timecard} classifiers={allClassifiers} />
            </div>
            <SectionEditProvider canEdit={timecard.isEditable}>
                <div className={cn(RECORD_GRID_CLASS, 'px-(--page-gutter-x) pt-6 pb-(--page-gutter-y)')}>
                    <div className="flex min-w-0 flex-col gap-(--card-gap)">
                        <TimecardSection timecard={timecard} rows={readOnlyRows}>
                            {(edit) => (
                                // Laid out as the card's form is: the one grid, a group's title a block of its
                                // own across it and not a cell, so the title is the same box over the read rows
                                // and over the inputs.
                                <SectionCard title={<FormattedMessage id="timecards.timecard" />} action={edit}>
                                    <div className={FIELD_GRID_CLASS}>
                                        <dl className="contents">
                                            <GridCells rows={editable(timecardRows)} />
                                        </dl>
                                        <TimecardGroupTitle id="timecards.form.description" />
                                        <dl className="contents">
                                            <GridCells rows={descriptionRows} />
                                        </dl>
                                        <TimecardGroupTitle id="timecards.time-and-billing" />
                                        <dl className="contents">
                                            <GridCells rows={editable(billingRows)} />
                                        </dl>
                                        <TimecardGroupTitle id="expenses.expenses" />
                                        <dl className="contents">
                                            <GridCells rows={[expensesRow, ...readOnlyRows]} />
                                        </dl>
                                    </div>
                                </SectionCard>
                            )}
                        </TimecardSection>
                        <AuditInfo
                            createdAt={timecard.createdAt}
                            createdByEmployee={timecard.createdByEmployee}
                            lastModifiedAt={timecard.lastModifiedAt}
                            changedByEmployee={timecard.changedByEmployee}
                        />
                    </div>
                    <div className="flex min-w-0 flex-col gap-(--card-gap)">
                        <GridItemList
                            isNew
                            titleId="invoices.connected-invoices"
                            data={[
                                {
                                    label: '',
                                    wide: true,
                                    content: (
                                        <TimeCardInvoiceList
                                            invoices={timecard.invoices ?? []}
                                            classifiers={classifiers}
                                        />
                                    ),
                                },
                            ]}
                        />
                        {timecard.timerLogs && timecard.timerLogs.length > 0 ? (
                            <GridItemList
                                isNew
                                titleId="timecards.timer-logs"
                                data={[
                                    {
                                        label: '',
                                        wide: true,
                                        content: <TimerLogsList timerLogs={timecard.timerLogs} />,
                                    },
                                ]}
                            />
                        ) : null}
                    </div>
                </div>
            </SectionEditProvider>
        </PaddedLayout>
    );
}
